<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useInstantBookingSlotsStore, useUsersStore, useRestaurantsStore } from '@/stores'
import { loadRmStripe } from '@/utils/stripe'
import axios from '@axios'
import moment from 'moment-timezone'
import confetti from 'canvas-confetti'
import PreferredIcon from '@core/components/PreferredIcon.vue'
import { generateGeneralReservationUrlWithQueryString } from '@/plugins/misc'

// Card brand images
import amexCard from '@/assets/images/payment_methods/amex.png'
import cirrusCard from '@/assets/images/payment_methods/cirrus.png'
import dinersCard from '@/assets/images/payment_methods/diners.png'
import discoverCard from '@/assets/images/payment_methods/discover.png'
import jcbCard from '@/assets/images/payment_methods/jcb.png'
import maestroCard from '@/assets/images/payment_methods/maestro.png'
import masterCard from '@/assets/images/payment_methods/mastercard.png'
import unionpayCard from '@/assets/images/payment_methods/unionpay.png'
import visaCard from '@/assets/images/payment_methods/visa.png'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  slotId: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'success'])

const cardBrandImages = {
  amex: amexCard,
  cirrus: cirrusCard,
  diners: dinersCard,
  discover: discoverCard,
  jcb: jcbCard,
  maestro: maestroCard,
  mastercard: masterCard,
  unionpay: unionpayCard,
  visa: visaCard
}

const router = useRouter()
const instantBookingSlotsStore = useInstantBookingSlotsStore()
const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()

const { myUser, loginDialogMode } = storeToRefs(usersStore)
const { currentSlot, loading } = storeToRefs(instantBookingSlotsStore)
const { viewingDetailsRestaurant } = storeToRefs(restaurantsStore)

// Form state
const specialRequests = ref('')
const acceptTerms = ref(false)
const submitting = ref(false)
const processingPayment = ref(false)

// Payment state
const paymentMethods = ref([])
const selectedMethod = ref(null)
const showNewCard = ref(false)
const stripe = ref(null)
const cardElement = ref(null)
const elements = ref(null)
const newCardComplete = ref(false)

// Error handling
const errorDialog = ref(false)
const errorMessage = ref('')

// Track if card element is mounted
const cardElementMounted = ref(false)

// Get restaurant data
const restaurant = computed(() => {
  if (!currentSlot.value) return null
  
  return restaurantsStore.restaurants.find(r => r._id === currentSlot.value.restaurantId) || {
    name: currentSlot.value.restaurantName,
    market: currentSlot.value.restaurantMarket
  }
})

// Close dialog handler
const closeDialog = () => {
  emit('update:modelValue', false)
}

// Handle upgrade membership click - close dialog and open login/join dialog
const handleUpgradeMembership = () => {
  closeDialog()
  loginDialogMode.value = 'activate'
}

// Open restaurant details dialog
const openRestaurantDetails = () => {
  if (restaurant.value) {
    viewingDetailsRestaurant.value = restaurant.value
  }
}

// Check if user is Platinum
const isPlatinum = computed(() => {
  return myUser.value?.isPlatinum || false
})

// Profile validation - same fields as EditReservationDialog
const missingProfileFields = computed(() => {
  const missing = []
  if (!myUser.value) return missing

  if (!myUser.value.phoneNumber) missing.push('Phone number')
  if (!myUser.value.profileImageId) missing.push('Profile Pic')

  if (!myUser.value.verified) {
    if (!myUser.value.company) missing.push('Company')
    if (!myUser.value.title) missing.push('Position/Title')
  }

  return missing
})

const isProfileIncomplete = computed(() => {
  return missingProfileFields.value.length > 0
})

const profileValidationMessage = computed(() => {
  if (missingProfileFields.value.length === 0) return ''

  return `Additional details required to book: ${missingProfileFields.value.join(', ')}`
})

// Check if restaurant is Preferred
const isPreferred = computed(() => {
  return restaurant.value?.status === 'Preferred'
})

// General availability URL for checking on restaurant's platform
const generalAvailabilityUrl = computed(() => {
  if (!currentSlot.value || !restaurant.value?.generalReservationUrl) return null

  const tz = currentSlot.value.restaurantTimeZone || 'America/New_York'

  // Build a reservation-like object from currentSlot data
  // Format date in restaurant's timezone to ensure correct date is used
  const reservationData = {
    date: moment.tz(currentSlot.value.date, tz).format('YYYY-MM-DD'),
    preferredTime: formattedTime.value.toLowerCase().replace(' ', ''), // Convert "7:00 PM" to "7:00pm"
    partySize: currentSlot.value.partySize
  }

  return generateGeneralReservationUrlWithQueryString(restaurant.value, reservationData)
})

// Get the platform name for display
const generalReservationPlatform = computed(() => {
  return restaurant.value?.generalReservationPlatform || 'restaurant website'
})

// Format date and time
const formattedDate = computed(() => {
  if (!currentSlot.value) return ''
  const tz = currentSlot.value.restaurantTimeZone || 'America/New_York'
  
  return moment.tz(currentSlot.value.date, tz).format('dddd, MMMM D, YYYY')
})

const formattedTime = computed(() => {
  if (!currentSlot.value?.time) return ''
  const time = currentSlot.value.time
  const parsed = moment(time, ['HH:mm', 'H:mm', 'h:mm A', 'h:mm a'])
  if (parsed.isValid()) {
    return parsed.format('h:mm A')
  }
  
  return time
})

// Calculate cancellation deadline
const cancellationDeadline = computed(() => {
  if (!currentSlot.value) return ''
  const tz = currentSlot.value.restaurantTimeZone || 'America/New_York'
  const dateStr = currentSlot.value.date
  const timeStr = currentSlot.value.time

  const timeParsed = moment(timeStr, ['HH:mm', 'H:mm', 'h:mm A', 'h:mm a', 'HH:mm:ss'], false)
  if (!timeParsed.isValid()) {
    const reservationTime = moment.tz(dateStr, tz)
    const deadline = reservationTime.subtract(currentSlot.value.cancellationPeriodHours, 'hours')
    
    return deadline.format('MMMM D, YYYY [at] h:mm A z')
  }

  const reservationTime = moment.tz(dateStr, tz)
    .hour(timeParsed.hour())
    .minute(timeParsed.minute())
    .second(0)

  const deadline = reservationTime.clone().subtract(currentSlot.value.cancellationPeriodHours, 'hours')
  
  return deadline.format('MMMM D, YYYY [at] h:mm A z')
})

// Format currency with half-up rounding to 2 decimal places
const formatCurrency = amount => {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(rounded)
}

// Calculate pricing breakdown
const pricingBreakdown = computed(() => {
  if (!currentSlot.value) return null

  const slot = currentSlot.value
  const baseSpend = slot.guaranteedSpendPerPerson * slot.partySize
  const tax = baseSpend * (slot.taxPercentage / 100)
  const tip = baseSpend * (slot.tipPercentage / 100)
  const ccFee = (baseSpend + tax + tip) * (slot.ccFeePercentage / 100)
  const total = baseSpend + tax + tip + ccFee

  return {
    baseSpend,
    tax,
    taxPercentage: slot.taxPercentage,
    tip,
    tipPercentage: slot.tipPercentage,
    ccFee,
    ccFeePercentage: slot.ccFeePercentage,
    total
  }
})

// Calculate penalty amount per person in dollars
const penaltyAmountPerPerson = computed(() => {
  if (!currentSlot.value) return 0

  const penalty = currentSlot.value.penalty
  const guaranteedSpendPerPerson = currentSlot.value.guaranteedSpendPerPerson

  if (penalty && penalty.type === 'fixed') {
    return penalty.amount
  }

  if (penalty && penalty.type === 'percentage') {
    return guaranteedSpendPerPerson * (penalty.amount / 100)
  }

  return 0
})

// Calculate total penalty amount
const totalPenaltyAmount = computed(() => {
  if (!currentSlot.value) return 0
  
  return penaltyAmountPerPerson.value * currentSlot.value.partySize
})

// Form validation
const isFormValid = computed(() => {
  return acceptTerms.value &&
    (selectedMethod.value || (showNewCard.value && newCardComplete.value))
})

// Initialize Stripe
const initializeStripe = async () => {
  try {
    if (stripe.value) return

    // RM-313: the key comes from the server that will process the card.
    stripe.value = await loadRmStripe()

    if (!stripe.value) {
      throw new Error('Failed to load Stripe')
    }

    elements.value = stripe.value.elements()
  } catch (error) {
    console.error('Failed to initialize Stripe:', error)
    showError('Failed to load payment form. Please refresh the page.')
  }
}

// Mount the card element to the DOM
const mountCardElement = async () => {
  if (cardElementMounted.value && cardElement.value) {
    try {
      cardElement.value.unmount()
    } catch (e) {
      // Ignore unmount errors
    }
    cardElement.value.destroy()
    cardElement.value = null
    cardElementMounted.value = false
  }

  await initializeStripe()

  if (!elements.value) return

  cardElement.value = elements.value.create('card', {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  })

  cardElement.value.on('change', event => {
    newCardComplete.value = event.complete
  })

  await nextTick()

  const cardElementContainer = document.getElementById('card-element-dialog')
  if (cardElementContainer) {
    cardElement.value.mount('#card-element-dialog')
    cardElementMounted.value = true
  }
}

// Load payment methods
const loadPaymentMethods = async () => {
  try {
    const res = await axios.get('/checkout/payment-methods', {
      params: {
      }
    })
    paymentMethods.value = res.data

    if (paymentMethods.value.length === 0) {
      showNewCard.value = true
      await mountCardElement()
    } else {
      selectedMethod.value = paymentMethods.value[0].id
    }
  } catch (error) {
    console.error('Failed to load payment methods:', error)
    showError('Failed to load payment methods. Please refresh the page.')
  }
}

// Add new card
const handleAddNewCard = async () => {
  showNewCard.value = true
  selectedMethod.value = null
  newCardComplete.value = false
  await mountCardElement()
}

// Cleanup card element
const unmountCardElement = () => {
  if (cardElementMounted.value && cardElement.value) {
    try {
      cardElement.value.unmount()
    } catch (e) {
      // Ignore unmount errors
    }
    cardElement.value.destroy()
    cardElement.value = null
    cardElementMounted.value = false
    newCardComplete.value = false
  }
}

// Show error dialog
const showError = message => {
  errorMessage.value = message
  errorDialog.value = true
}

// Share restaurant page
const shareUrl = computed(() => {
  if (!restaurant.value?.profileId) return null
  
  return `${window.location.origin}/r/${restaurant.value.profileId}`
})

const copied = ref(false)

const handleShare = async () => {
  if (!shareUrl.value) return

  if (navigator.share) {
    try {
      await navigator.share({
        title: currentSlot.value?.restaurantName || 'Restaurant',
        url: shareUrl.value
      })
      
      return
    } catch (e) {
      if (e.name === 'AbortError') return
    }
  }

  try {
    await navigator.clipboard.writeText(shareUrl.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (e) {
    console.error('Failed to copy:', e)
  }
}

// Reset form state
const resetFormState = () => {
  specialRequests.value = ''
  acceptTerms.value = false
  submitting.value = false
  processingPayment.value = false
  paymentMethods.value = []
  selectedMethod.value = null
  showNewCard.value = false
  newCardComplete.value = false
  errorDialog.value = false
  errorMessage.value = ''
  unmountCardElement()
}

// Handle booking submission
const handleSubmit = async () => {
  if (!isFormValid.value || submitting.value || processingPayment.value) return

  processingPayment.value = true

  try {
    let paymentMethodId = selectedMethod.value

    if (showNewCard.value && !selectedMethod.value) {
      if (!cardElementMounted.value || !cardElement.value) {
        throw new Error('Card form is not ready. Please try again.')
      }

      const { error, paymentMethod } = await stripe.value.createPaymentMethod({
        type: 'card',
        card: cardElement.value,
      })

      if (error) {
        throw new Error(error.message)
      }

      paymentMethodId = paymentMethod.id

      await axios.post('/checkout/save-payment-method', {
        paymentMethodId,
        jwt: JSON.parse(localStorage.getItem('jwt'))
      })
    }

    submitting.value = true

    const result = await instantBookingSlotsStore.bookSlot(
      props.slotId,
      paymentMethodId,
      specialRequests.value
    )

    await usersStore.getMyUser()

    // Success! Show confetti and navigate
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    })

    emit('success', result)

    setTimeout(() => {
      closeDialog()
      router.push('/bookings')
    }, 1000)

  } catch (error) {
    console.error('Booking failed:', error)
    submitting.value = false
    processingPayment.value = false

    // Extract error message from axios response or error object
    const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || ''

    if (errorMsg.includes('no longer available')) {
      showError(
        'This slot was just booked by another member. Please select another time.'
      )

      // Navigate to restaurant profile with Book Now tab
      const restaurantProfileId = restaurant.value?.profileId
      setTimeout(() => {
        closeDialog()
        if (restaurantProfileId) {
          router.push(`/r/${restaurantProfileId}?tab=Book%20Now`)
        }
      }, 3000)
    } else if (errorMsg.includes('payment')) {
      showError(
        'Payment could not be processed. Please try another card or contact support.'
      )
    } else {
      showError(
        errorMsg || 'An error occurred while booking. Please try again.'
      )
    }
  }
}

// Initialize when dialog opens
const initializeCheckout = async () => {
  if (!props.slotId) return

  resetFormState()

  await instantBookingSlotsStore.fetchSlotById(props.slotId)

  // Use populated restaurant data from slot response (works without auth)
  if (currentSlot.value?.restaurant) {
    const existing = restaurantsStore.restaurants.find(r => r._id === currentSlot.value.restaurant._id)
    if (existing) Object.assign(existing, currentSlot.value.restaurant)
    else restaurantsStore.restaurants.push(currentSlot.value.restaurant)
  }

  if (currentSlot.value?.status !== 'available') {
    showError('This slot is no longer available.')
    const restaurantProfileId = restaurant.value?.profileId
    setTimeout(() => {
      closeDialog()
      if (restaurantProfileId) {
        router.push(`/r/${restaurantProfileId}?tab=Book%20Now`)
      }
    }, 2000)

    return
  }

  if (isPlatinum.value) {
    await loadPaymentMethods()
  }

  // Fetch full restaurant data if logged in (may include additional fields)
  if (currentSlot.value?.restaurantId) {
    await restaurantsStore.getRestaurantById(currentSlot.value.restaurantId)
  }
}

// Watch for dialog open/close
watch(() => props.modelValue, async isOpen => {
  if (isOpen && props.slotId) {
    await initializeCheckout()
  } else if (!isOpen) {
    resetFormState()
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  unmountCardElement()
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    fullscreen
    :scrim="false"
    transition="dialog-bottom-transition"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <VCard class="instant-booking-checkout-dialog">
      <!-- Close Button -->
      <VBtn
        icon
        variant="text"
        class="close-btn"
        @click="closeDialog"
      >
        <VIcon
          icon="mdi-close"
          size="24"
        />
      </VBtn>

      <div class="dialog-content">
        <!-- Loading State -->
        <div
          v-if="loading"
          class="loading-state"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="text-body-1 text-medium-emphasis mt-4">
            Loading booking details...
          </p>
        </div>

        <!-- Submitting State -->
        <div
          v-else-if="submitting"
          class="loading-state"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="text-body-1 text-medium-emphasis mt-4">
            Confirming your booking...
          </p>
        </div>

        <!-- Checkout Form -->
        <div
          v-else-if="currentSlot"
          class="checkout-content"
        >
          <!-- Back Navigation -->
          <button
            class="back-button"
            @click="closeDialog"
          >
            <VIcon
              icon="mdi-arrow-left"
              size="18"
            />
            <span>Back</span>
          </button>

          <div class="checkout-columns">
            <!-- Left Column - Restaurant & Details -->
            <div class="checkout-left">
              <!-- Restaurant Header Card -->
              <div class="restaurant-card">
                <div class="restaurant-header">
                  <PreferredIcon
                    v-if="isPreferred"
                    :size="24"
                    class="preferred-icon"
                  />
                  <h1
                    class="restaurant-name restaurant-name-clickable"
                    @click="openRestaurantDetails"
                  >
                    {{ currentSlot.restaurantName }}
                  </h1>
                  <VBtn
                    v-if="shareUrl"
                    icon
                    variant="text"
                    size="x-small"
                    :title="copied ? 'Link copied!' : 'Share restaurant'"
                    @click="handleShare"
                  >
                    <VIcon
                      :icon="copied ? 'mdi-check' : 'ion-share-outline'"
                      size="18"
                      :color="copied ? 'primary-dark' : undefined"
                    />
                  </VBtn>
                </div>
                <p class="restaurant-address">
                  {{ restaurant?.address || currentSlot.restaurantMarket }}
                </p>

                <div
                  v-if="currentSlot.experienceName"
                  class="experience-badge"
                >
                  {{ currentSlot.experienceName }}
                </div>

                <p class="experience-description">
                  Enjoy ordering off {{ currentSlot.restaurantName }}'s regular <a
                    href="#"
                    class="menu-link"
                  >menu(s)</a>.
                  This reservation is secured with a {{ formatCurrency(pricingBreakdown?.baseSpend || 0) }} ({{
                    formatCurrency(currentSlot.guaranteedSpendPerPerson) }}pp) Food &amp; Beverage minimum.
                </p>

                <!-- Check General Availability Link -->
                <a
                  v-if="generalAvailabilityUrl"
                  :href="generalAvailabilityUrl"
                  target="_blank"
                  class="general-availability-link"
                >
                  check general availability ({{ generalReservationPlatform }})
                  <VIcon
                    size="14"
                    class="ml-1"
                    icon="mdi-open-in-new"
                  />
                </a>
              </div>

              <!-- Reservation Details Card -->
              <div class="details-card">
                <h2 class="details-title">
                  Reservation Details
                </h2>
                <div class="details-list">
                  <p class="detail-item">
                    {{ formattedDate }}
                  </p>
                  <p class="detail-item">
                    {{ formattedTime }}
                  </p>
                  <p class="detail-item">
                    Party of {{ currentSlot.partySize }}
                  </p>
                  <p
                    v-if="currentSlot.seatingArea"
                    class="detail-item"
                  >
                    {{ currentSlot.seatingArea }}
                  </p>
                  <p
                    v-if="pricingBreakdown"
                    class="detail-item detail-bold"
                  >
                    {{ formatCurrency(pricingBreakdown.baseSpend) }} ({{
                      formatCurrency(currentSlot.guaranteedSpendPerPerson) }}pp) F&amp;B minimum
                  </p>
                  <p
                    v-if="pricingBreakdown"
                    class="detail-item detail-light"
                  >
                    Tax ({{ pricingBreakdown.taxPercentage }}%): {{ formatCurrency(pricingBreakdown.tax) }} | Gratuity ({{
                      pricingBreakdown.tipPercentage }}%): {{ formatCurrency(pricingBreakdown.tip) }}
                  </p>
                  <p
                    v-if="pricingBreakdown"
                    class="detail-item detail-bold"
                  >
                    Total: {{ formatCurrency(pricingBreakdown.total) }}
                  </p>
                  <p class="detail-item detail-light">
                    100% of your spend goes to {{ currentSlot.restaurantName }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Right Column - Payment & Actions -->
            <div class="checkout-right">
              <!-- How Payment Works -->
              <div class="payment-info-section">
                <div class="payment-info-header">
                  <VIcon
                    icon="mdi-shield-check-outline"
                    size="22"
                    color="primary-dark"
                  />
                  <span class="payment-info-title">How payment works</span>
                </div>

                <div class="payment-info-body">
                  <div class="payment-info-main">
                    <div class="payment-info-amount">
                      {{ formatCurrency(pricingBreakdown?.total || 0) }}
                    </div>
                    <div class="payment-info-desc">
                      temporary credit card hold with RM (F&B minimum + tax & tip)
                    </div>
                  </div>

                  <p class="payment-info-note">
                    You'll pay the bill directly at <strong>{{ currentSlot.restaurantName }}</strong>.
                    Your hold will be released within 24 hours after your reservation if you meet the {{ formatCurrency(pricingBreakdown?.baseSpend || 0) }} F&amp;B minimum.
                  </p>

                  <details class="payment-info-details">
                    <summary>What if I spend below the minimum?</summary>
                    <div class="payment-info-details-content">
                      <p>{{ currentSlot.restaurantName }} can either:</p>
                      <ul>
                        <li>Charge a flat fee of {{ formatCurrency(pricingBreakdown?.baseSpend || 0) }} for the F&amp;B minimum + tax &amp; tip</li>
                        <li>Receive the remainder via your credit card hold on RM</li>
                      </ul>
                    </div>
                  </details>
                </div>
              </div>

              <!-- Cancellation Policy -->
              <div class="policy-section">
                <div class="policy-header">
                  <VIcon
                    icon="mdi-alert-circle-outline"
                    size="20"
                    class="policy-icon"
                  />
                  <span class="policy-title">Cancellation Policy</span>
                </div>
                <p class="policy-text">
                  Full refund if canceled before <strong>{{ cancellationDeadline }}</strong>
                </p>
                <p class="policy-penalty">
                  Cancellations after this time or no shows will result in a {{ formatCurrency(penaltyAmountPerPerson)
                  }}pp penalty ({{ formatCurrency(totalPenaltyAmount) }} total)
                </p>
              </div>

              <!-- Platinum Required Message (for non-Platinum users) -->
              <template v-if="!isPlatinum">
                <div class="platinum-required">
                  <p class="platinum-message">
                    Platinum membership required to book Premium tables
                  </p>
                  <div
                    v-if="!myUser || !myUser.value || myUser.value.userLevel === 'dummy'"
                    class="platinum-actions"
                  >
                    <VBtn
                      color="primary"
                      variant="flat"
                      @click="loginDialogMode = 'activate'"
                    >
                      Request to join
                    </VBtn>
                    <VBtn
                      color="#efefef"
                      style="color: #222222;"
                      variant="flat"
                      @click="loginDialogMode = 'login'"
                    >
                      Log in
                    </VBtn>
                  </div>
                  <VBtn
                    v-else
                    color="primary"
                    variant="flat"
                    @click="handleUpgradeMembership"
                  >
                    Upgrade Membership
                  </VBtn>
                </div>
              </template>

              <!-- Credit Card Section -->
              <div
                v-if="isPlatinum"
                class="card-section"
              >
                <div class="card-header">
                  <VIcon
                    icon="mdi-credit-card-outline"
                    size="20"
                    class="card-icon"
                  />
                  <span class="card-title">Credit card on hold</span>
                </div>
                <div class="card-content">
                  <!-- Saved Cards -->
                  <VSelect
                    v-if="paymentMethods.length > 0 && !showNewCard"
                    v-model="selectedMethod"
                    :items="paymentMethods.map(pm => ({
                      title: `${pm.card.brand.toUpperCase()} •••• ${pm.card.last4}`,
                      value: pm.id
                    }))"
                    label="Add/select a card"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                  >
                    <template #prepend-item>
                      <VListItem @click="handleAddNewCard">
                        <template #prepend>
                          <VIcon icon="mdi-plus" />
                        </template>
                        <VListItemTitle>Add New Card</VListItemTitle>
                      </VListItem>
                      <VDivider class="my-2" />
                    </template>
                  </VSelect>

                  <!-- New Card Form -->
                  <div v-if="showNewCard || paymentMethods.length === 0">
                    <div
                      id="card-element-dialog"
                      class="card-element-container"
                    />
                    <VBtn
                      v-if="paymentMethods.length > 0"
                      variant="text"
                      color="primary"
                      size="small"
                      class="mt-2"
                      @click="unmountCardElement(); showNewCard = false; selectedMethod = paymentMethods[0].id"
                    >
                      Use Saved Card
                    </VBtn>
                  </div>
                </div>
              </div>

              <!-- Confirmation Checkbox -->
              <div
                v-if="isPlatinum && !isProfileIncomplete"
                class="confirm-section"
              >
                <VCheckbox
                  v-model="acceptTerms"
                  hide-details
                  color="primary"
                  density="compact"
                >
                  <template #label>
                    <span class="confirm-label">
                      I confirm this booking is for me only &amp; agree to the credit card hold &amp; cancellation policy
                    </span>
                  </template>
                </VCheckbox>
              </div>

              <!-- Profile Incomplete Warning -->
              <VAlert
                v-if="isPlatinum && isProfileIncomplete"
                type="warning"
                class="mb-4"
              >
                <div>{{ profileValidationMessage }}</div>
                <div class="mt-2">
                  questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
                </div>
              </VAlert>

              <!-- Complete Profile Button (when profile incomplete) -->
              <VBtn
                v-if="isPlatinum && isProfileIncomplete"
                color="primary-dark"
                size="x-large"
                block
                class="book-button"
                @click="closeDialog(); usersStore.editMyUser()"
              >
                Complete Profile
              </VBtn>

              <!-- Book Now Button -->
              <VBtn
                v-else
                color="primary-dark"
                size="x-large"
                block
                class="book-button"
                :disabled="!isFormValid || submitting || processingPayment"
                :loading="submitting || processingPayment"
                @click="handleSubmit"
              >
                Book Now
              </VBtn>

              <!-- Questions -->
              <div class="questions-section">
                <p>questions?</p>
                <p>
                  email <a
                    href="mailto:preferred@reserverm.com"
                    class="email-link"
                  >preferred@reserverm.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Dialog -->
        <VDialog
          v-model="errorDialog"
          max-width="400"
        >
          <VCard>
            <VCardTitle class="bg-error text-white">
              <VIcon
                icon="mdi-alert-circle"
                class="mr-2"
              />
              Booking Error
            </VCardTitle>
            <VCardText class="pa-4">
              <p class="text-body-1 mb-0">
                {{ errorMessage }}
              </p>
            </VCardText>
            <VCardActions>
              <VSpacer />
              <VBtn
                color="primary"
                variant="flat"
                @click="errorDialog = false"
              >
                Close
              </VBtn>
            </VCardActions>
          </VCard>
        </VDialog>
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
$primary-teal: var(--ds-brand-primary-dark);
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);
$text-light: var(--ds-text-light);
$border-light: var(--ds-border-light);
$bg-white: var(--ds-surface-card);
$bg-light: var(--ds-bg-light);

.instant-booking-checkout-dialog {
  background-color: $bg-light;
  position: relative;
}

.close-btn {
  position: fixed;
  top: var(--ds-space-4);
  right: var(--ds-space-4);
  z-index: var(--ds-z-dropdown);
  background: $bg-white !important;
  box-shadow: var(--ds-shadow-sm);
}

.dialog-content {
  min-height: 100vh;
  padding: var(--ds-space-6) var(--ds-space-4);
  padding-top: 60px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.checkout-content {
  max-width: 1100px;
  margin: 0 auto;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--ds-space-2) var(--ds-space-4) var(--ds-space-2) 10px;
  margin-bottom: var(--ds-space-4);
  background: $bg-white;
  border: 1px solid $border-light;
  border-radius: var(--ds-radius-sm);
  color: $text-medium;
  font-size: var(--ds-text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--ds-duration-normal) ease;

  &:hover {
    background: $bg-light;
    border-color: $text-light;
    color: $text-dark;
  }

  &:active {
    transform: translateX(-2px);
  }
}

.checkout-columns {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: var(--ds-space-6);
  align-items: start;
}

.checkout-left {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
}

.restaurant-card {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-6);
  border: 1px solid $border-light;
}

.restaurant-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  margin-bottom: var(--ds-space-1);
}

.preferred-icon {
  flex-shrink: 0;
}

.restaurant-name {
  font-size: 1.75rem;
  font-weight: 600;
  color: $primary-teal;
  margin: 0;
  line-height: var(--ds-leading-tight);
}

.restaurant-name-clickable {
  cursor: pointer;
  transition: opacity var(--ds-duration-normal) ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

.restaurant-address {
  font-size: var(--ds-text-base);
  color: $text-medium;
  margin: 0 0 var(--ds-space-4) 0;
}

.experience-badge {
  display: inline-block;
  padding: var(--ds-space-2) var(--ds-space-4);
  border: 1.5px solid $primary-teal;
  border-radius: var(--ds-radius-xl);
  color: $primary-teal;
  font-size: var(--ds-text-sm);
  font-weight: 500;
  margin-bottom: var(--ds-space-4);
}

.experience-description {
  font-size: var(--ds-text-base);
  color: $text-dark;
  line-height: var(--ds-leading-normal);
  margin: 0;
}

.menu-link {
  color: $primary-teal;
  text-decoration: underline;
}

.general-availability-link {
  display: inline-flex;
  align-items: center;
  margin-top: var(--ds-space-4);
  font-size: var(--ds-text-base);
  color: $primary-teal;
  text-decoration: none;
  transition: opacity var(--ds-duration-normal) ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

.details-card {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-6);
  border: 1px solid $border-light;
}

.details-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: $text-dark;
  margin: 0 0 var(--ds-space-4) 0;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item {
  font-size: var(--ds-text-base);
  color: $text-dark;
  margin: 0;
  line-height: var(--ds-leading-normal);
}

.detail-bold {
  font-weight: 600;
}

.detail-light {
  color: $text-light;
  font-size: var(--ds-text-base);
}

.checkout-right {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
}

.payment-info-section {
  background: linear-gradient(135deg, #f8fafa 0%, #f0f7f6 100%);
  border: 1px solid #e0ebe9;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-5);
}

.payment-info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: var(--ds-space-4);
}

.payment-info-title {
  font-size: var(--ds-text-base);
  font-weight: 600;
  color: $text-dark;
}

.payment-info-main {
  display: flex;
  align-items: baseline;
  gap: var(--ds-space-2);
  margin-bottom: var(--ds-space-3);
}

.payment-info-amount {
  font-size: var(--ds-text-2xl);
  font-weight: 700;
  color: $primary-teal;
}

.payment-info-desc {
  font-size: var(--ds-text-base);
  color: $text-medium;
}

.payment-info-note {
  font-size: var(--ds-text-sm);
  color: var(--ds-text-secondary);
  line-height: var(--ds-leading-normal);
  margin: 0 0 var(--ds-space-3) 0;

  strong {
    font-weight: 600;
  }
}

.payment-info-details {
  summary {
    font-size: var(--ds-text-sm);
    color: $primary-teal;
    cursor: pointer;
    font-weight: 500;
    list-style: none;
    display: flex;
    align-items: center;
    gap: var(--ds-space-1);

    &::-webkit-details-marker {
      display: none;
    }

    &::before {
      content: '▸';
      font-size: 0.7rem;
      transition: transform var(--ds-duration-normal) ease;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &[open] summary::before {
    transform: rotate(90deg);
  }
}

.payment-info-details-content {
  margin-top: 10px;
  padding-left: var(--ds-space-3);
  border-left: 2px solid #e0ebe9;

  p {
    font-size: var(--ds-text-sm);
    color: $text-medium;
    margin: 0 0 6px 0;
  }

  ul {
    margin: 0 0 0 var(--ds-space-4);
    padding: 0;
    font-size: var(--ds-text-sm);
    color: $text-medium;
    list-style-type: disc;

    li {
      margin-bottom: var(--ds-space-1);
      line-height: var(--ds-leading-normal);

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.policy-section {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-5);
  border: 1px solid $border-light;
}

.policy-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  margin-bottom: var(--ds-space-2);
}

.policy-icon {
  color: $text-medium;
}

.policy-title {
  font-size: var(--ds-text-base);
  font-weight: 500;
  color: $text-dark;
}

.policy-text {
  font-size: var(--ds-text-base);
  color: $text-dark;
  margin: 0 0 var(--ds-space-1) 0;
  padding-left: 28px;
  line-height: var(--ds-leading-normal);

  strong {
    font-weight: 600;
  }
}

.policy-penalty {
  font-size: var(--ds-text-sm);
  color: $text-light;
  margin: 0;
  padding-left: 28px;
  line-height: var(--ds-leading-normal);
}

.card-section {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-5);
  border: 1px solid $border-light;
}

.card-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  margin-bottom: var(--ds-space-4);
}

.card-icon {
  color: $text-medium;
}

.card-title {
  font-size: var(--ds-text-base);
  font-weight: 500;
  color: $text-dark;
}

.card-content {
  width: 100%;
}

.card-element-container {
  border: 1px solid $border-light;
  border-radius: var(--ds-radius-sm);
  padding: 14px;
  background: $bg-white;

  &:focus-within {
    border-color: $primary-teal;
    box-shadow: 0 0 0 1px $primary-teal;
  }
}

.confirm-section {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-4) var(--ds-space-5);
  border: 1px solid $border-light;
}

.confirm-label {
  font-size: var(--ds-text-base);
  color: $text-medium;
  line-height: var(--ds-leading-normal);
}

.book-button {
  border-radius: var(--ds-radius-md) !important;
  text-transform: none !important;
  font-size: var(--ds-text-md) !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  height: 56px !important;
}

.questions-section {
  text-align: center;
  padding: var(--ds-space-4) 0;

  p {
    margin: 0;
    font-size: var(--ds-text-base);
    color: $text-medium;
    line-height: 1.6;
  }
}

.email-link {
  color: $primary-teal;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.platinum-required {
  background: #fff8e6;
  border: 1px solid #f5d77a;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-5);
}

.platinum-message {
  font-size: var(--ds-text-base);
  font-weight: 500;
  color: #8a6d00;
  margin: 0 0 var(--ds-space-4) 0;
}

.platinum-actions {
  display: flex;
  gap: var(--ds-space-3);
  flex-wrap: wrap;
}

@media (max-width: 900px) {
  .checkout-columns {
    grid-template-columns: 1fr;
  }

  .dialog-content {
    padding: var(--ds-space-4) var(--ds-space-3);
    padding-top: 60px;
  }

  .restaurant-name {
    font-size: var(--ds-text-2xl);
  }
}
</style>
